import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Space, useNotify } from 'ebs-design';
import { useVoipAPI } from 'api';
import type { VoipItem, VoipListParams, VoipListResponse } from 'api/voip';

interface LoginPbxModalProps {
  open: boolean;
  onClose: () => void;
}

const defaultVoipListParams: VoipListParams = { page: 1, per_page: 10 };

export const LoginPbxModal: React.FC<LoginPbxModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const { getVoipList, loginVoip } = useVoipAPI();

  const { data: voipData } = useQuery<VoipListResponse>(
    ['voip', defaultVoipListParams],
    getVoipList,
    { enabled: open },
  );

  const {
    mutate: loginVoipMutate,
    isLoading: isLoginVoipLoading,
    variables,
  } = useMutation((id: number) => loginVoip(id), {
    onError: () => notify.error({ title: t('error.someThingIsWrong') }),
    onSuccess: () => {
      notify.success({ title: t('calls.login_pbx_success') });
      onClose();
    },
  });

  const onLoginPbx = (item: VoipItem): void => loginVoipMutate(item.id);

  if (!open) return null;

  return (
    <Modal open header={t('calls.login_pbx')} onClose={onClose} size="small">
      <Modal.Content>
        <Space direction="vertical" size={15}>
          {(voipData?.results ?? []).map((item) => (
            <Space key={item.id} justify="space-between" size={15} style={{ width: '100%' }}>
              <span>{item.name}</span>

              <Button
                type="primary"
                onClick={() => onLoginPbx(item)}
                loading={isLoginVoipLoading && variables === item.id}
                className="ebs-button"
              >
                {t('calls.login')}
              </Button>
            </Space>
          ))}

          {voipData?.results?.length === 0 && <span>{t('calls.login_pbx_no_items')}</span>}
        </Space>
      </Modal.Content>

      <Modal.Footer>
        <Button onClick={onClose}>{t('buttons.cancel')}</Button>
      </Modal.Footer>
    </Modal>
  );
};
